import {
  SUPPLY_CHAIN_OVERVIEW_CONFIGURE_STORAGE_KEY,
  SUPPLY_CHAIN_OVERVIEW_LAST_CUSTOM_DATE,
  SUPPLY_CHAIN_OVERVIEW_LAST_CUSTOM_PERIOD,
  SUPPLY_CHAIN_OVERVIEW_LAST_PERIOD,
  SUPPLY_CHAIN_OVERVIEW_LAST_TAB,
  SUPPLY_CHAIN_OVERVIEW_LAYOUTS_DEFAULT,
  SUPPLY_CHAIN_OVERVIEW_VERSION_KEY,
} from "../../../constants";
import { IPeriod } from "../../../models/overview/dashboard";
import {
  ESupplyChainOverviewTabs,
  SupplyChainOverviewConfigure,
} from "../../../models/supply.chain.overview.configure";
import { DashboardPeriodOptions } from "../../../util/enums";

/**
 * The version value must be made by the date in format yyyyMMdd of the last change in
 * defaultConfig
 *
 * Please, fill it JUST WITH NUMBERS of a date in format yyyyMMdd.
 *
 * Please, update the switch case code in the function updateLocalStorageConfig to apply the changes.
 * */
const CURRENT_VERSION = 20240605;

export const defaultConfig: SupplyChainOverviewConfigure = {
  containers: {
    atDC: true,
    atPort: true,
    containerDistributionByDestination: true,
    containersByArrivalDate: true,
    containersStatuses: true,
    inbound: true,
    layouts: { md: SUPPLY_CHAIN_OVERVIEW_LAYOUTS_DEFAULT.containers },
    layoutsDisabled: {},
  },
  orders: {
    lastReceivedOrders: true,
    layouts: { md: SUPPLY_CHAIN_OVERVIEW_LAYOUTS_DEFAULT.orders },
    layoutsDisabled: {},
    pendingPurchaseOrders: true,
    poConfirmedVSPoReceived: true,
    poStatuses: true,
    shipByDate: true,
  },
  products: {
    layouts: { md: SUPPLY_CHAIN_OVERVIEW_LAYOUTS_DEFAULT.products },
    layoutsDisabled: {},
    mostRequestedProducts: true,
    productsOrigin: true,
    productsRunningOutOfStock: true,
  },
  shipments: {
    layouts: { md: SUPPLY_CHAIN_OVERVIEW_LAYOUTS_DEFAULT.shipments },
    layoutsDisabled: {},
    shipmentsPerDate: true,
    shipmentStatuses: true,
    shipmentsTracked: true,
  },
};

const updateLocalStorageConfig = (config: any, version: number): SupplyChainOverviewConfigure => {
  switch (version) {
    case 20240408:
      config.shipments.shipmentsPerDate = config.shipments.shipmentsPerTime;
      delete config.shipments.shipmentsPerTime;
      for (const key of Object.keys(config.shipments.layouts)) {
        const shipmentsPerTimeIdx = config.shipments.layouts[key].findIndex(
          ({ i }: any) => i === "shipmentsPerTime"
        );

        if (shipmentsPerTimeIdx >= 0) {
          config.shipments.layouts[key][shipmentsPerTimeIdx].i = "shipmentsPerDate";
        }
      }

    // eslint-disable-next-line no-fallthrough
    case 20240605:
      /**
       * 1st Step: Delete properties that were removed in the update. Ignore if there isn't anyone
       * For example:
       *
       * delete config.containers.atDC;
       */

      /**
       * 2nd Step: Add properties and values that were created in the update. Ignore if there isn't anyone
       * For example:
       *
       * config.containers.atDC = false;
       */

      /**
       * Include here another case clause (WITHOUT BREAK) and follow the 1st and 2nd steps idea to delete
       * and include properties for passing by version to version until the current.
       */

      /**
       * After updating the object from the previous version to the current one, remember to save
       * the current version number and the updated configuration in the local storage before returning
       * the updated object.
       *
       * localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_VERSION_KEY, CURRENT_VERSION.toString());
       * localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_CONFIGURE_STORAGE_KEY, JSON.stringify(config));
       */

      localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_VERSION_KEY, CURRENT_VERSION.toString());
      localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_CONFIGURE_STORAGE_KEY, JSON.stringify(config));

      return config;

    default: // Saving and returning a defaultConfig, just in case to not identify a predicted version.
      localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_VERSION_KEY, CURRENT_VERSION.toString());
      localStorage.setItem(
        SUPPLY_CHAIN_OVERVIEW_CONFIGURE_STORAGE_KEY,
        JSON.stringify(defaultConfig)
      );

      return defaultConfig;
  }
};

export const getLocalStorageConfig = (): SupplyChainOverviewConfigure => {
  const configVersion = localStorage.getItem(SUPPLY_CHAIN_OVERVIEW_VERSION_KEY);
  const storedConfig = localStorage.getItem(SUPPLY_CHAIN_OVERVIEW_CONFIGURE_STORAGE_KEY);

  if (!configVersion || !storedConfig) {
    localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_VERSION_KEY, CURRENT_VERSION.toString());
    localStorage.setItem(
      SUPPLY_CHAIN_OVERVIEW_CONFIGURE_STORAGE_KEY,
      JSON.stringify(defaultConfig)
    );

    return defaultConfig;
  }

  const versionNumber = Number(configVersion);

  if (versionNumber < CURRENT_VERSION) {
    return updateLocalStorageConfig(JSON.parse(storedConfig), Number(configVersion));
  }

  return JSON.parse(storedConfig);
};

export const setLocalStorageConfig = (config: SupplyChainOverviewConfigure): void => {
  localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_CONFIGURE_STORAGE_KEY, JSON.stringify(config));
};

export const getLocalStorageLastTab = (): ESupplyChainOverviewTabs => {
  const lastTab = localStorage.getItem(SUPPLY_CHAIN_OVERVIEW_LAST_TAB) as ESupplyChainOverviewTabs;

  if (!lastTab) {
    localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_LAST_TAB, ESupplyChainOverviewTabs.ORDERS);

    return ESupplyChainOverviewTabs.ORDERS;
  }

  return lastTab;
};

export const setLocalStorageLastTab = (tab: ESupplyChainOverviewTabs): void => {
  localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_LAST_TAB, tab);
};

export const getLocalStorageLastPeriod = (): DashboardPeriodOptions => {
  const lastPeriod = localStorage.getItem(
    SUPPLY_CHAIN_OVERVIEW_LAST_PERIOD
  ) as DashboardPeriodOptions;

  if (!lastPeriod) {
    localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_LAST_PERIOD, DashboardPeriodOptions.MONTH_TO_DATE);

    return DashboardPeriodOptions.MONTH_TO_DATE;
  }

  return lastPeriod;
};

export const setLocalStorageLastPeriod = (period: DashboardPeriodOptions): void => {
  localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_LAST_PERIOD, period);
};

export const getLocalStorageLastCustomDate = (): Date | undefined => {
  const lastCustomDate = localStorage.getItem(SUPPLY_CHAIN_OVERVIEW_LAST_CUSTOM_DATE);

  if (!lastCustomDate) {
    return undefined;
  }

  return new Date(lastCustomDate);
};

export const setLocalStorageLastCustomDate = (customDate?: Date): void => {
  if (customDate) {
    localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_LAST_CUSTOM_DATE, customDate.toISOString());
  } else {
    localStorage.removeItem(SUPPLY_CHAIN_OVERVIEW_LAST_CUSTOM_DATE);
  }
};

export const getLocalStorageLastCustomPeriod = (): IPeriod | undefined => {
  const lastCustomPeriod = localStorage.getItem(SUPPLY_CHAIN_OVERVIEW_LAST_CUSTOM_PERIOD);

  if (!lastCustomPeriod) {
    return undefined;
  }

  const { from, to } = JSON.parse(lastCustomPeriod);

  return { from: new Date(from), to: new Date(to) };
};

export const setLocalStorageLastCustomPeriod = (customPeriod?: IPeriod): void => {
  if (customPeriod) {
    localStorage.setItem(SUPPLY_CHAIN_OVERVIEW_LAST_CUSTOM_PERIOD, JSON.stringify(customPeriod));
  } else {
    localStorage.removeItem(SUPPLY_CHAIN_OVERVIEW_LAST_CUSTOM_PERIOD);
  }
};
